/**
 * @flow
 */

import React, { useCallback, useState } from 'react';
import { Modal, Header, Button, Icon } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { GET_CABINETS_QUERY } from './CabinetList';

const DELETE_CABINET_QUERY = gql`
    mutation deleteCabinet($input: DeleteCabinetInput!) {
        deleteCabinet(input: $input) {
            cabinetId
        }
    }
`;

const DeleteCabinetDialog = ({ cabinet, trigger, open, onClose }) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(({ intl }) => intl.get('locale'));
    const searchQuery = useSelector(({ staff }) => staff.get('searchQuery'));

    const [deleteCabinet, { loading }] = useMutation(DELETE_CABINET_QUERY);
    const [deleteFailed, setDeleteFailed] = useState(false);

    const onDelete = useCallback(() => {
        setDeleteFailed(false);
        // No optimisticResponse: the row stays visible while the request is
        // in flight, so the modal can show an error if the server rejects it.
        // On success the cache update removes the row, naturally unmounting
        // this component — no need to call onClose() in .then().
        deleteCabinet({
            variables: {
                input: {
                    id: cabinet.id,
                },
            },
            update: (store, { data: { deleteCabinet: result } }) => {
                const data = store.readQuery({
                    query: GET_CABINETS_QUERY,
                    variables: {
                        salon,
                        search: searchQuery || undefined,
                        lang: currentLanguage,
                    },
                });

                if (
                    data &&
                    data.viewer &&
                    data.viewer.salon &&
                    data.viewer.salon.cabinets
                ) {
                    store.writeQuery({
                        query: GET_CABINETS_QUERY,
                        variables: {
                            salon,
                            search: searchQuery || undefined,
                            lang: currentLanguage,
                        },
                        data: {
                            ...data,
                            viewer: {
                                ...data.viewer,
                                salon: {
                                    ...data.viewer.salon,
                                    cabinets: data.viewer.salon.cabinets.filter(
                                        c => c.id !== result.cabinetId,
                                    ),
                                },
                            },
                        },
                    });
                }
            },
        }).catch(() => {
            setDeleteFailed(true);
        });
    }, [deleteCabinet, salon, searchQuery, currentLanguage, cabinet]);

    return (
        <Modal trigger={trigger} open={open} onClose={onClose} size="small" basic>
            <Header icon>
                <Icon name="remove" />
                <FormattedMessage
                    id="pages.cabinets.DeleteCabinet.header"
                    defaultMessage="Delete Cabinet"
                />
            </Header>
            <Modal.Content>
                <FormattedMessage
                    id="pages.cabinets.DeleteCabinet.text"
                    defaultMessage="Are you sure you want to delete {cabinetName} cabinet?"
                    values={{
                        cabinetName: cabinet.name,
                    }}
                />
                {deleteFailed && (
                    <p>
                        <FormattedMessage
                            id="errors.defaultMessage"
                            defaultMessage="Something went wrong"
                        />
                    </p>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} color="red" basic inverted disabled={loading}>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.cabinets.DeleteCabinet.no"
                        defaultMessage="No"
                    />
                </Button>
                <Button onClick={onDelete} color="green" inverted loading={loading} disabled={loading}>
                    <Icon name="checkmark" />
                    <FormattedMessage
                        id="pages.cabinets.DeleteCabinet.yes"
                        defaultMessage="Yes"
                    />
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default DeleteCabinetDialog;
